// Environment-based API URL configuration
const getApiBaseUrl = (): string => {
  console.log("API BASE URL:", process.env.REACT_APP_API_BASE_URL);
  console.log("ENVIRONMENT:", process.env.REACT_APP_ENVIRONMENT);
  console.log("NODE_ENV:", process.env.NODE_ENV);

  // Check for environment variable first
  if (process.env.REACT_APP_API_BASE_URL) {
    return process.env.REACT_APP_API_BASE_URL;
  }
  console.log("API BASE URL2:", process.env.REACT_APP_API_BASE_URL);
  console.log("ENVIRONMENT:", process.env.REACT_APP_ENVIRONMENT);
  console.log("NODE_ENV:", process.env.NODE_ENV);

  return (
    process.env.REACT_APP_API_BASE_URL ||
    "https://backend-test.enpropel.com"
  );
};

/** Host or host+/api — trailing slashes stripped */
export const API_BASE_URL = getApiBaseUrl().replace(/\/+$/, "");

/**
 * Spring uses `server.servlet.context-path=/api`. Build `…/api` exactly once:
 * if env is already `http://localhost:8080/api`, do not append `/api` again (avoids `/api/api/jobs/...`).
 */
export const API_ROOT = API_BASE_URL.toLowerCase().endsWith("/api")
  ? API_BASE_URL
  : `${API_BASE_URL}/api`;

// Log the current API URL for debugging
console.log(`🌍 Environment: ${process.env.NODE_ENV}`);
console.log(`🔗 API Base URL: ${API_BASE_URL}`);
console.log(`🔗 API root (REST): ${API_ROOT}`);

export const apiUrls = {
  // Health check
  health: `${API_ROOT}/health`,

  // Home Page endpoints
  homePage: {
    getAllSuccessStories: `${API_BASE_URL}/public/homepage/success-stories?limit={limit}`,
    getCorporatePartners: `${API_BASE_URL}/public/homepage/corporate-partners`,
    getAcademiaPartners: `${API_BASE_URL}/public/homepage/academia-partners`,
  },

  newsMedia: {
    summary: `${API_BASE_URL}/news-media/summary`,
    getById: (id: number | string) => `${API_BASE_URL}/news-media/get/${id}`,
  },

  /** Program endpoints — list and detail are public; all others require auth */
  program: {
    /** Public listing of all active programs */
    list: `${API_BASE_URL}/program/list`,
    detail: (programCode: string) =>
      `${API_BASE_URL}/program/${encodeURIComponent(programCode)}/get`,
    validateEligibility: `${API_BASE_URL}/program/validate-eligibility`,
    validateParticipantEligibility: `${API_BASE_URL}/program/validate-participant-eligibility`,
    enroll: `${API_BASE_URL}/program/enroll`,
    paymentInitiate: `${API_BASE_URL}/program/payment/initiate`,
    paymentConfirm: `${API_BASE_URL}/program/payment/confirm`,
    studentLookup: `${API_BASE_URL}/program/student-lookup`,
    /** Authenticated student's enrolled programs with per-stage activity progress */
    myEnrollments: `${API_BASE_URL}/program/my-enrollments`,
    /** Update activity selections for one stage of an enrollment */
    updateEnrollmentActivities: (enrollmentCode: string, stageId: number) =>
      `${API_BASE_URL}/program/enrollment/${encodeURIComponent(enrollmentCode)}/stage/${stageId}/activities`,
    updateQuizActivityCompletion: (enrollmentCode: string, stageId: number, activityId: number) =>
      `${API_BASE_URL}/program/enrollment/${encodeURIComponent(enrollmentCode)}/stage/${stageId}/activities/${activityId}/completion`,
    submitHackathonStage2: (enrollmentCode: string, stageId: number, activityId: number) =>
      `${API_BASE_URL}/program/enrollment/${encodeURIComponent(enrollmentCode)}/stage/${stageId}/activities/${activityId}/hackathon-submission`,
  },

  // Authentication
  login: `${API_BASE_URL}/auth/login`,
  registerStudent: `${API_BASE_URL}/auth/register/student`,
  registerOrganization: `${API_BASE_URL}/auth/register/organization`,
  registerMentor: `${API_BASE_URL}/auth/register/mentor`,
  registerSponsor: `${API_BASE_URL}/auth/register/sponsor`,
  checkUser: `${API_BASE_URL}/auth/check-user`,
  refreshToken: `${API_BASE_URL}/auth/refreshtoken`,

  // Password Management
  password: {
    sendOtp: `${API_BASE_URL}/password/otp/send`,
    verifyOtp: `${API_BASE_URL}/password/otp/verify`,
    resendOtp: `${API_BASE_URL}/password/otp/resend`,
    reset: `${API_BASE_URL}/password/reset`,
    forgotPasswordComplete: `${API_BASE_URL}/password/forgotpassword/complete`,
  },

  // Dropdowns endpoints
  dropdowns: {
    states: `${API_BASE_URL}/master/states`,
    citiesbystate: `${API_BASE_URL}/master/cities/{stateCode}`,
    citiesbystatecode: `${API_BASE_URL}/master/citiesbystatecode/{stateCode}`,
    masterdata: `${API_BASE_URL}/master/{masterdata}/list`,
    masterdatabyparent: `${API_BASE_URL}/master/{masterdata}/list/{parentcode}`,
  },

  // Internships endpoints (updated to match backend structure)
  internships: {
    list: `${API_BASE_URL}/internships/list`,
    byOrgId: (orgId: number) => `${API_BASE_URL}/internships/org/${orgId}/list`,
    byLoginOrgId: `${API_BASE_URL}/internships/org/list`,
    create: `${API_BASE_URL}/internships/create`,
    update: (internshipCodeOrId: string | number) => `${API_BASE_URL}/internships/${internshipCodeOrId}/update`,
    delete: (internshipCodeOrId: string | number) => `${API_BASE_URL}/internships/${internshipCodeOrId}/delete`,
    details: (internshipCodeOrId: string | number) => `${API_BASE_URL}/internships/${internshipCodeOrId}/get`,
    detailsByCode: (internshipCode: string) => `${API_BASE_URL}/internships/code/${internshipCode}/get`,
    apply: (internshipCodeOrId: string | number) => `${API_BASE_URL}/internships/${internshipCodeOrId}/apply`,
    applyValidate: (internshipCodeOrId: string | number) =>
      `${API_BASE_URL}/internships/${internshipCodeOrId}/apply/validate`,
    applyDocUpload: (internshipCodeOrId: string | number) =>
      `${API_BASE_URL}/internships/${internshipCodeOrId}/apply/doc-upload`,
    applyValidateCoupon: (internshipCodeOrId: string | number) =>
      `${API_BASE_URL}/internships/${internshipCodeOrId}/apply/validate-coupon`,
    applyValidateByCode: (internshipCode: string) =>
      `${API_BASE_URL}/internships/code/${internshipCode}/apply/validate`,
    applyDocUploadByCode: (internshipCode: string) =>
      `${API_BASE_URL}/internships/code/${internshipCode}/apply/doc-upload`,
    applyValidateCouponByCode: (internshipCode: string) =>
      `${API_BASE_URL}/internships/code/${internshipCode}/apply/validate-coupon`,
    applySubmit: (internshipCodeOrId: string | number) =>
      `${API_BASE_URL}/internships/${internshipCodeOrId}/apply/submit`,
    applySubmitByCode: (internshipCode: string) =>
      `${API_BASE_URL}/internships/code/${internshipCode}/apply/submit`,
    stats: `${API_BASE_URL}/internships/stats`,
    addEligibility: (internshipCodeOrId: string | number) =>
      `${API_BASE_URL}/internships/${internshipCodeOrId}/eligibility/save`,
    eligibilityList: (internshipCodeOrId: string | number) =>
      `${API_BASE_URL}/internships/${internshipCodeOrId}/eligibility/list`,
    deleteEligibility: (eligibilityId: number) =>
      `${API_BASE_URL}/internships/eligibility/${eligibilityId}/delete`,
    addSkill: (internshipCodeOrId: string | number) =>
      `${API_BASE_URL}/internships/${internshipCodeOrId}/skill/save`,
    skillList: (internshipCodeOrId: string | number) =>
      `${API_BASE_URL}/internships/${internshipCodeOrId}/skill/list`,
    deleteSkill: (skillId: number) =>
      `${API_BASE_URL}/internships/internship/${skillId}/delete`,
    addMultipleDocuments: (internshipCodeOrId: string | number) =>
      `${API_BASE_URL}/internships/${internshipCodeOrId}/document/save`,
    documentList: (internshipCodeOrId: string | number) =>
      `${API_BASE_URL}/internships/${internshipCodeOrId}/document/list`,
    deleteDocument: (documentId: number) =>
      `${API_BASE_URL}/internships/document/${documentId}/delete`,
    submit: (internshipCodeOrId: string | number) => `${API_BASE_URL}/internships/${internshipCodeOrId}/submit`,
    uploadDocument: `${API_BASE_URL}/internships/document/upload`,
    updateStatus: `${API_BASE_URL}/internships/status/update`,
  },

  jobs: {
    /** POST — public job board; no auth (published jobs only on server). */
    publicList: `${API_ROOT}/jobs/public/list`,
    /** GET — public job detail (skills, eligibility); no auth. */
    publicDetails: (id: number | string) => `${API_ROOT}/jobs/public/${id}`,
    /** GET — public org profile picture for job board avatars; no auth. */
    publicOrgAvatar: (orgId: number | string) =>
      `${API_ROOT}/jobs/public/organization/${encodeURIComponent(String(orgId))}/avatar`,
    /** POST org-scoped job list (same endpoint as byLoginOrgId). */
    list: `${API_ROOT}/jobs/org/list`,
    byLoginOrgId: `${API_ROOT}/jobs/org/list`,
    create: `${API_ROOT}/jobs/create`,
    update: `${API_ROOT}/jobs/update`,
    details: (jobId: number | string) =>
      `${API_ROOT}/jobs/${encodeURIComponent(String(jobId))}/get`,
    eligibilityList: (jobId: number | string) =>
      `${API_ROOT}/jobs/${encodeURIComponent(String(jobId))}/eligibilities`,
    eligibilityAdd: (jobId: number | string) =>
      `${API_ROOT}/jobs/${encodeURIComponent(String(jobId))}/eligibilities`,
    eligibilityUpdate: (jobId: number | string, eligibilityId: number | string) =>
      `${API_ROOT}/jobs/${encodeURIComponent(String(jobId))}/eligibilities/${encodeURIComponent(String(eligibilityId))}`,
    eligibilityDelete: (jobId: number | string, eligibilityId: number | string) =>
      `${API_ROOT}/jobs/${encodeURIComponent(String(jobId))}/eligibilities/${encodeURIComponent(String(eligibilityId))}`,
    skillList: (jobId: number | string) =>
      `${API_ROOT}/jobs/${encodeURIComponent(String(jobId))}/skills`,
    skillAdd: (jobId: number | string) =>
      `${API_ROOT}/jobs/${encodeURIComponent(String(jobId))}/skills`,
    skillUpdate: (jobId: number | string, skillId: number | string) =>
      `${API_ROOT}/jobs/${encodeURIComponent(String(jobId))}/skills/${encodeURIComponent(String(skillId))}`,
    skillDelete: (jobId: number | string, skillId: number | string) =>
      `${API_ROOT}/jobs/${encodeURIComponent(String(jobId))}/skills/${encodeURIComponent(String(skillId))}`,
    dashboardSummary: `${API_ROOT}/jobs/dashboard/summary`,
    dashboardRecent: `${API_ROOT}/jobs/dashboard/recent`,
    dashboardRecentApplications: `${API_ROOT}/jobs/dashboard/recent-applications`,
    applicationsList: `${API_ROOT}/jobs/org/applications/list`,
    apply: (jobId: number | string) =>
      `${API_ROOT}/jobs/${encodeURIComponent(String(jobId))}/apply`,
    applyValidate: (jobId: number | string) =>
      `${API_ROOT}/jobs/${encodeURIComponent(String(jobId))}/apply/validate`,
    applicationCheck: (jobId: number | string) =>
      `${API_ROOT}/dashboard/student/job-applications/check?jobId=${encodeURIComponent(String(jobId))}`,
    updateStatus: `${API_ROOT}/jobs/status/update`,
    applicationStatusUpdate: (applicationId: string) =>
      `${API_ROOT}/jobs/applications/${encodeURIComponent(applicationId)}/status`,
    applicationDetailOrg: (applicationId: string) =>
      `${API_ROOT}/dashboard/jobs/applications/org/${encodeURIComponent(applicationId)}`,
    delete: (jobId: number | string) =>
      `${API_ROOT}/jobs/${encodeURIComponent(String(jobId))}/delete`,
  },

  // User Profile endpoints
  userProfile: {
    personalInfo: {
      get: `${API_BASE_URL}/userprofile/personalinfo/get`,
      update: `${API_BASE_URL}/userprofile/personalinfo/update`,
      profilePictureUpload: `${API_BASE_URL}/userprofile/personalinfo/profile-picture`,
      recalculateCompletion: `${API_BASE_URL}/userprofile/completion/recalculate`,
      syncCompletion: `${API_BASE_URL}/userprofile/completion/sync`,
      /** Raw image bytes for resume PDF (avoids S3 CORS) */
      profilePictureContent: `${API_BASE_URL}/userprofile/personalinfo/profile-picture-content`,
      /** ADMIN/ORGANIZATION: applicant/student bytes by public id (for org PDF download) */
      profilePictureContentByUserUniqueId: (userUniqueId: string) =>
        `${API_BASE_URL}/userprofile/personalinfo/${encodeURIComponent(userUniqueId)}/profile-picture-content`,
      getById: (userUniqueId: string) =>
        `${API_BASE_URL}/userprofile/personalinfo/${userUniqueId}/get`,
      updateById: (userUniqueId: string) =>
        `${API_BASE_URL}/userprofile/personalinfo/${userUniqueId}/update`,
    },
    address: {
      addOrUpdate: `${API_BASE_URL}/userprofile/address/addorupdate`,
    },
    education: {
      list: `${API_BASE_URL}/userprofile/usereducation/list`,
      add: `${API_BASE_URL}/userprofile/usereducation/add`,
      update: `${API_BASE_URL}/userprofile/usereducation/update`,
      delete: (educationId: number) =>
        `${API_BASE_URL}/userprofile/usereducation/${educationId}/delete`,
      getById: (userUniqueId: string) =>
        `${API_BASE_URL}/userprofile/usereducation/${userUniqueId}/get`,
    },
    userskills: {
      list: `${API_BASE_URL}/userprofile/userskills/list`,
      add: `${API_BASE_URL}/userprofile/userskills/add`,
      update: `${API_BASE_URL}/userprofile/userskills/update`,
      delete: (skillId: number) =>
        `${API_BASE_URL}/userprofile/userskills/${skillId}/delete`,
      getById: (userUniqueId: string) =>
        `${API_BASE_URL}/userprofile/userskills/${userUniqueId}/get`,
    },
    workExperience: {
      list: `${API_BASE_URL}/userprofile/workexperience/list`,
      add: `${API_BASE_URL}/userprofile/workexperience/add`,
      update: `${API_BASE_URL}/userprofile/workexperience/update`,
      delete: (workExperienceId: number) =>
        `${API_BASE_URL}/userprofile/workexperience/${workExperienceId}/delete`,
      getById: (userUniqueId: string) =>
        `${API_BASE_URL}/userprofile/workexperience/${userUniqueId}/get`,
    },
    organization: {
      get: `${API_BASE_URL}/userprofile/organization/get`,
      update: `${API_BASE_URL}/userprofile/organization/update`,
      updateAddress: `${API_BASE_URL}/userprofile/organization/update/address`,
    },
  },

  mentorProfile: {
    personalInfo: {
      get: `${API_BASE_URL}/userprofile/personalinfo/get`,
      update: `${API_BASE_URL}/userprofile/personalinfo/update`,
      profilePictureUpload: `${API_BASE_URL}/userprofile/personalinfo/profile-picture`,
      /** Raw image bytes for resume PDF (avoids S3 CORS) */
      profilePictureContent: `${API_BASE_URL}/userprofile/personalinfo/profile-picture-content`,
      /** ADMIN/ORGANIZATION: applicant/student bytes by public id (for org PDF download) */
      profilePictureContentByUserUniqueId: (userUniqueId: string) =>
        `${API_BASE_URL}/userprofile/personalinfo/${encodeURIComponent(userUniqueId)}/profile-picture-content`,
      getById: (userUniqueId: string) =>
        `${API_BASE_URL}/userprofile/personalinfo/${userUniqueId}/get`,
      updateById: (userUniqueId: string) =>
        `${API_BASE_URL}/userprofile/personalinfo/${userUniqueId}/update`,
    },
    address: {
      addOrUpdate: `${API_BASE_URL}/userprofile/address/addorupdate`,
    },
    education: {
      list: `${API_BASE_URL}/userprofile/usereducation/list`,
      add: `${API_BASE_URL}/userprofile/usereducation/add`,
      update: `${API_BASE_URL}/userprofile/usereducation/update`,
      delete: (educationId: number) =>
        `${API_BASE_URL}/userprofile/usereducation/${educationId}/delete`,
      getById: (userUniqueId: string) =>
        `${API_BASE_URL}/userprofile/usereducation/${userUniqueId}/get`,
    },
    workExperience: {
      list: `${API_BASE_URL}/userprofile/workexperience/list`,
      add: `${API_BASE_URL}/userprofile/workexperience/add`,
      update: `${API_BASE_URL}/userprofile/workexperience/update`,
      delete: (workExperienceId: number) =>
        `${API_BASE_URL}/userprofile/workexperience/${workExperienceId}/delete`,
      getById: (userUniqueId: string) =>
        `${API_BASE_URL}/userprofile/workexperience/${userUniqueId}/get`,
    },
    organization: {
      get: `${API_BASE_URL}/userprofile/organization/get`,
      update: `${API_BASE_URL}/userprofile/organization/update`,
    },
    changePassword: `${API_ROOT}/password/reset`,
    preference: {
      list: `${API_BASE_URL}/mentorship/mentor/preferences/list`,
      listInactive: `${API_BASE_URL}/mentorship/mentor/preferences/list/inactive`,
      add: `${API_BASE_URL}/mentorship/mentor/preferences/create`,
      update: `${API_BASE_URL}/mentorship/mentor/preferences/update`,
      delete: () => `${API_BASE_URL}/mentorship/mentor/preferences/delete`,
      activate: `${API_BASE_URL}/mentorship/mentor/preferences/active`,
      deactivate: `${API_BASE_URL}/mentorship/mentor/preferences/inactive`,
      getById: (userUniqueId: string) =>
        `${API_BASE_URL}/mentorship/mentor/preferences/${userUniqueId}/get`,
    },
  },

  organization: {
    profile: `${API_BASE_URL}/organization/profile`,
    internshipSummaryCounts: `${API_BASE_URL}/dashboard/internships/summarycounts`,
    internshipStatusWiseCounts: `${API_BASE_URL}/dashboard/internships/statuswisecounts`,
    applicationSummaryCounts: `${API_BASE_URL}/dashboard/applications/summarycounts`,
    applicationStatusWiseCounts: `${API_BASE_URL}/dashboard/applications/statuswisecounts`,
    applicationStudentProfile: (applicationId: string) => `${API_BASE_URL}/dashboard/application/${applicationId}/student-profile`,
  },

  // Partner Enquiry endpoints
  partnerEnquiry: {
    submit: `${API_BASE_URL}/partner-enquiry/submit`,
    list: `${API_BASE_URL}/partner-enquiry/list`,
    getByStatus: (status: string) => `${API_BASE_URL}/partner-enquiry/status/${status}`,
    getById: (id: number) => `${API_BASE_URL}/partner-enquiry/${id}`,
  },

  // Professional Services Enquiry endpoints
  professionalServicesEnquiry: {
    submit: `${API_BASE_URL}/professional-services-enquiry/submit`,
    list: `${API_BASE_URL}/professional-services-enquiry/list`,
    getByStatus: (status: string) => `${API_BASE_URL}/professional-services-enquiry/status/${status}`,
    getById: (id: number) => `${API_BASE_URL}/professional-services-enquiry/${id}`,
    getByContact: `${API_BASE_URL}/professional-services-enquiry/by-contact`,
    getByContactId: (id: number) => `${API_BASE_URL}/professional-services-enquiry/by-contact/${id}`,
  },

  // Message Center endpoints
  messageCenter: {
    submit: `${API_BASE_URL}/message-center/submit`,
    getMyMessages: (email: string) => `${API_BASE_URL}/message-center/my-messages?email=${encodeURIComponent(email)}`,
    getMyMessagesForUser: `${API_BASE_URL}/message-center/my-messages/user`,
    getById: (id: number) => `${API_BASE_URL}/message-center/${id}`,
    getByMessageNumber: (messageNumber: string) => `${API_BASE_URL}/message-center/message-number/${messageNumber}`,
    addReply: `${API_BASE_URL}/message-center/reply`,
    getReplies: (messageCenterId: number) => `${API_BASE_URL}/message-center/${messageCenterId}/replies`,
    markAsRead: (id: number) => `${API_BASE_URL}/message-center/${id}/mark-read`,
    markAsUnread: (id: number) => `${API_BASE_URL}/message-center/${id}/mark-unread`,
    markAllAsRead: `${API_BASE_URL}/message-center/mark-all-read`,
  },

  notifications: {
    list: `${API_BASE_URL}/notifications`,
    markAsRead: (id: number | string) => `${API_BASE_URL}/notifications/${id}/read`,
    unreadCount: `${API_BASE_URL}/notifications/unread-count`,
  },

  sponsorship: {
    summaryCounts: `${API_BASE_URL}/sponsorship/summarycounts`,
    publicSummaryCounts: `${API_BASE_URL}/sponsorship/public/summarycounts`,
    summaryCountsMine: `${API_BASE_URL}/sponsorship/summarycounts/mine`,
    config: `${API_BASE_URL}/sponsorship/config`,
    add: `${API_BASE_URL}/sponsorship/add`,
    update: (id: string) => `${API_BASE_URL}/sponsorship/${id}/update`,
    list: (page: number, size: number) => `${API_BASE_URL}/sponsorship/list?page=${page}&size=${size}`,
    publicList: (page: number, size: number) => `${API_BASE_URL}/sponsorship/public/list?page=${page}&size=${size}`,
    beneficiariesList: (page: number, size: number) => `${API_BASE_URL}/sponsorship/beneficiaries/list?page=${page}&size=${size}`,
    publicBeneficiariesList: (page: number, size: number) => `${API_BASE_URL}/sponsorship/public/beneficiaries/list?page=${page}&size=${size}`,
    getById: (id: string) => `${API_BASE_URL}/sponsorship/${id}/get`,
    getBeneficiaryDetails: (id: string) => `${API_BASE_URL}/sponsorship/beneficiaries/${id}/get`,
    paymentStatusUpdate: `${API_BASE_URL}/sponsorship/payment/status/update`,
    paymentInitiate: `${API_BASE_URL}/sponsorship/payment/initiate`,
    paymentConfirm: `${API_BASE_URL}/sponsorship/payment/confirm`,
    recentActivity: `${API_BASE_URL}/sponsorship/activity/recent`,
  },

  subscription: {
    plans: `${API_BASE_URL}/subscription/plans`,
    myCurrent: `${API_BASE_URL}/subscription/my-current`,
    freePlanStatus: `${API_BASE_URL}/subscription/free-plan-status`,
    cancel: `${API_BASE_URL}/subscription/cancel`,
    history: `${API_BASE_URL}/subscription/history`,
    paymentHistory: `${API_BASE_URL}/subscription/payment/history`,
    paymentInitiate: `${API_BASE_URL}/subscription/payment/initiate`,
    paymentConfirm: `${API_BASE_URL}/subscription/payment/confirm`,
  },

  // Mentorship endpoints
  mentorship: {
    base: `${API_BASE_URL}/mentorship`,
    mentors: {
      list: `${API_BASE_URL}/mentorship/mentors/list`,
      getById: (id: string) => `${API_BASE_URL}/mentorship/mentors/${id}`,
      getDetails: (mentorId: string) => `${API_BASE_URL}/mentorship/mentor/${mentorId}/get`,
      featured: (limit: number) => `${API_BASE_URL}/mentorship/mentors/featured?limit=${limit}`,
      filterOptions: `${API_BASE_URL}/mentorship/mentors/filter-options`,
      search: (query: string, limit: number) => `${API_BASE_URL}/mentorship/mentors/search?q=${encodeURIComponent(query)}&limit=${limit}`,
      byCategory: (category: string) => `${API_BASE_URL}/mentorship/mentors/category/${encodeURIComponent(category)}`,
      byCompany: (company: string) => `${API_BASE_URL}/mentorship/mentors/company/${encodeURIComponent(company)}`,
      stats: `${API_BASE_URL}/mentorship/mentors/stats`,
    },
    application: {
      submit: `${API_BASE_URL}/mentorship/application/submit`,
    },
  },
};
